import cx from "classnames";

import { titleCased } from "common/utils/strings";
import LinkButton from "common/components/LinkButton";
import CatalogImage from "common/components/CatalogImage";

import { useBreakpoints } from "client/common/hooks";
import TextChip from "client/common/components/content/TextChip";
import { urls } from "client/routes";
import { useSendPostHogItemClick } from "client/analytics/utils";
import { ItemClickSource } from "client/analytics/types";

import { PromotionItem } from "../types";

import styles from "../styles/PromotionItemCard.module.less";

type PromotionItemCardProps = {
  item: PromotionItem;
  size?: "s" | "m";
  classNames?: {
    product?: string;
    content?: string;
    image?: string;
    names?: string;
    name?: string;
    description?: string;
    facts?: string;
  };
  onNavigate?: () => void;
  clickSource?: ItemClickSource;
};

const PromotionItemCard = ({
  item,
  size = "m",
  classNames,
  onNavigate,
  clickSource,
}: PromotionItemCardProps) => {
  const { isPhoneAndSmaller } = useBreakpoints();
  const sendPostHogItemClick = useSendPostHogItemClick();

  const isSmall = size === "s" || isPhoneAndSmaller;

  const handleClick = () => {
    if (clickSource) {
      sendPostHogItemClick(item, { source: clickSource, unit_price: null });
    }
    if (onNavigate) {
      onNavigate();
    }
  };

  const facts = [
    { label: titleCased(item.product.product_line.product_select_label), value: item.product.name },
    { label: "Size", value: item.name },
  ];

  return (
    <LinkButton
      className={cx(styles.product, classNames?.product)}
      dest={urls.products.detail({
        params: { id: item.product.product_line.id },
        search: { item: item.id },
      })}
      onClick={handleClick}
    >
      <div
        className={cx(
          styles.background,
          item.patient_prescription_label && styles.prescribed,
          item.product.product_line.is_featured &&
            !item.patient_prescription_label &&
            styles.featured,
        )}
      >
        <div className={cx(styles.content, classNames?.content)}>
          <CatalogImage
            className={cx(styles.image, classNames?.image)}
            image={item.main_image}
            size="medium"
            alt="Item"
          />
          <div className={cx(styles.names, classNames?.names)}>
            {(item.product.product_line.is_featured || item.patient_prescription_label) &&
              (item.patient_prescription_label ? (
                <div className={styles.chip}>
                  <TextChip variant="info" size={isSmall ? "s" : "m"}>
                    {item.patient_prescription_label}
                  </TextChip>
                </div>
              ) : (
                <div className={styles.chip}>
                  <TextChip variant="highlight" size={isSmall ? "s" : "m"}>
                    Your Vet&apos;s Choice
                  </TextChip>
                </div>
              ))}
            <div className={cx(styles.name, classNames?.name)}>
              {item.product.product_line.name}
            </div>
            <div className={cx(styles.description, classNames?.description)}>
              {isSmall ? item.product_line_relative_name : item.product.product_line.description}
            </div>
          </div>
        </div>
      </div>
      {!isSmall && (
        <div className={cx(styles.facts, classNames?.facts)}>
          {facts.map((fact) => (
            <div className={styles.fact} key={fact.label}>
              <div className={styles.label}>{fact.label}</div>
              <div className={styles.value}>{fact.value}</div>
            </div>
          ))}
        </div>
      )}
    </LinkButton>
  );
};

export default PromotionItemCard;
